import { useState } from "react";
import type { QuestionnaireAnswers } from "@hearing/shared";
import { Layout } from "../components/Layout.js";
import { Button } from "../components/Button.js";
import { useScreener } from "../state.js";
import { useFlow } from "./useFlow.js";

interface QuestionDef {
  id: keyof QuestionnaireAnswers;
  label: string;
}

const QUESTIONS: QuestionDef[] = [
  {
    id: "noisyConversations",
    label: "Do you find it hard to have conversations in noisy environments?",
  },
  {
    id: "askToRepeat",
    label: "Do you find yourself having to ask others around you to repeat themselves?",
  },
  {
    id: "tvVolume",
    label: "Do you find yourself having to turn the TV up or rely on subtitles to hear?",
  },
  {
    id: "othersMumble",
    label: "Do you find that people around you mumble or don't speak clearly?",
  },
];

const OPTIONS = [
  { value: "yes", label: "Yes" },
  { value: "no", label: "No" },
  { value: "maybe", label: "Sometimes" },
];

export function Questions() {
  const { saveQuestionnaire } = useScreener();
  const { go } = useFlow();
  const [answers, setAnswers] = useState<QuestionnaireAnswers>({});

  const answered = QUESTIONS.every((q) => answers[q.id]);
  const answeredCount = QUESTIONS.filter((q) => answers[q.id]).length;

  function select(id: keyof QuestionnaireAnswers, value: string) {
    setAnswers((a) => ({ ...a, [id]: value }));
  }

  function submit() {
    saveQuestionnaire(answers);
    go("details");
  }

  return (
    <Layout step="questions">
      <div className="questions-header">
        <h2 className="center">About your everyday hearing</h2>
        <p className="muted center screen-copy">
          A few quick questions about listening situations help us interpret your
          result.
        </p>
        <div
          className="question-completion"
          role="progressbar"
          aria-label={`${answeredCount} of ${QUESTIONS.length} questions answered`}
          aria-valuemin={0}
          aria-valuemax={QUESTIONS.length}
          aria-valuenow={answeredCount}
        >
          <div className="question-completion-track" aria-hidden>
            <span style={{ width: `${(answeredCount / QUESTIONS.length) * 100}%` }} />
          </div>
          <span className="question-completion-label">
            {answeredCount} of {QUESTIONS.length} answered
          </span>
        </div>
      </div>

      <div className="question-list">
        {QUESTIONS.map((q, index) => (
          <div
            className={"field question-card" + (answers[q.id] ? " answered" : "")}
            key={q.id as string}
          >
            <span className="question-number">Question {index + 1}</span>
            <label>{q.label}</label>
            <div className="pill-row answer-row">
              {OPTIONS.map((opt) => {
                const selected = answers[q.id] === opt.value;
                return (
                  <button
                    key={opt.value}
                    type="button"
                    className={"pill answer-pill" + (selected ? " active" : "")}
                    onClick={() => select(q.id, opt.value)}
                    aria-pressed={selected}
                  >
                    {opt.label}
                  </button>
                );
              })}
            </div>
          </div>
        ))}
      </div>

      <div className="actions">
        <Button disabled={!answered} onClick={submit}>
          Continue
        </Button>
      </div>
    </Layout>
  );
}
